/**
 * Step 2 of the turn — re-validating every reference a narrowed action
 * carries against THIS turn's catalog (architecture.md §7.2).
 *
 * @remarks
 * Split out of `execute-turn.ts` at the 500-line cap when D232 gave actions
 * a second kind of reference. The parser's proposal may be a turn stale
 * (R6's clarification round-trip; a which-one answer about a thing the
 * player has since walked away from), so nothing acts on a reference the
 * current catalog does not hold: an item's (its id re-branded) or, since
 * D232, an actor's. D226 (E7): an `ask`'s subject may be a TOPIC — a thing
 * the player has met and is not standing beside — so for `ask` alone the
 * topic catalog counts as present. A question reaches nothing.
 *
 * @packageDocumentation
 */

import type { ActorId, ItemId, RuntimeState } from '@orpheus/content-schema';
import type { EntityRef } from '@orpheus/parser-engine';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { deriveEntityCatalog } from '../entities/derive-entity-catalog.js';
import { deriveTopicCatalog } from '../entities/derive-topic-catalog.js';
import { entityRefForActor, entityRefForItem } from '../entities/entity-ref.js';

/**
 * The first reference an action carries that is NOT in the current catalog,
 * if any — an item's (its id re-branded) or, D232, an actor's. D226 (E7): an
 * `ask`'s subject may be a TOPIC — a thing the player has met and is not
 * standing beside — so for `ask` alone the topic catalog counts as present.
 * A question reaches nothing.
 */
export const findReferenceOutsideCatalog = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: RuntimeAction,
): EntityRef | undefined => {
	const referencedRefs: readonly EntityRef[] = [
		...itemIdsReferencedBy(action).map((itemId) => entityRefForItem(itemId)),
		...actorIdsReferencedBy(action).map((actorId) => entityRefForActor(actorId)),
	];

	if (referencedRefs.length === 0) {
		return undefined;
	}

	const presentCatalog = deriveEntityCatalog(bundle, state);
	const referableCatalog = action.type === 'ask' ? [...presentCatalog, ...deriveTopicCatalog(bundle, state, presentCatalog)] : presentCatalog;
	const catalogRefs = new Set<string>(referableCatalog.map((entry) => entry.entityRef));

	return referencedRefs.find((entityRef) => !catalogRefs.has(entityRef));
};

/**
 * Which actor ids one action refers to (D232): the one looked at, and either
 * party of a question when it is a body, and every body an act reaches.
 *
 * @param action - The narrowed action
 * @returns The actor ids the action names
 */
const actorIdsReferencedBy = (action: RuntimeAction): readonly ActorId[] => {
	switch (action.type) {
		// D234: the struck body re-validates like the looked-at one — a foe the player walked away from is not one they can reach.
		// D235: and the body a thing is offered to — out of earshot, it cannot be offered anything.
		// D240: and the body gone through — one the player has left behind has nothing to search.
		// D251: and the body an authored verb is done to.
		case 'act': {
			return action.actorId === undefined ? [] : [action.actorId];
		}
		case 'ask': {
			return [action.addressee, action.subject].flatMap((party) => (party?.kind === 'actor' ? [party.actorId] : []));
		}
		case 'attack':
		case 'give':
		case 'inspect_actor':
		case 'search_actor':
		case 'show': {
			return [action.actorId];
		}
		// D252: and the body an order is given to — out of earshot, it cannot be told anything.
		case 'order': {
			return [action.actorId];
		}
		default: {
			return [];
		}
	}
};

/**
 * The actions that name no item at all, so there is nothing to re-validate:
 * the object-free directives — `wait` (D52) included, letting time pass
 * refers to no thing — and (D232, D240) a look at or a search of a body,
 * which `actorIdsReferencedBy` has.
 */
const NAMING_NO_ITEM = ['help', 'inspect_actor', 'inventory', 'look', 'map', 'search_actor', 'search_room', 'wait'] as const;

/** Whether an action names no item — a guard, so the switch below stays exhaustive over the rest (§3.5) and under the complexity cap. */
const namesNoItem = (action: RuntimeAction): action is Extract<RuntimeAction, { type: (typeof NAMING_NO_ITEM)[number] }> =>
	(NAMING_NO_ITEM as readonly string[]).includes(action.type);

/**
 * Which item ids one action refers to. Every action that names ONE item
 * names it as `itemId` — the door, switch, search, carry, and inspect verbs
 * alike — so that family is read structurally, and the exhaustive `switch`
 * (§3.5) covers only the actions whose references have other shapes.
 */
export const itemIdsReferencedBy = (action: RuntimeAction): readonly ItemId[] => {
	// D50: the container verbs name one item and re-validate it like any
	// other reference — a door you walked away from is not a door you can
	// still shut.
	if ('itemId' in action && action.itemId !== undefined) {
		return [action.itemId];
	}

	if (namesNoItem(action)) {
		return [];
	}

	switch (action.type) {
		// D251: an authored act done to a BODY names no item — `actorIdsReferencedBy` has it.
		case 'act': {
			return [];
		}
		// D231: both parties of a question re-validate when they are things —
		// the one addressed and the one asked about.
		case 'ask': {
			return [action.addressee, action.subject].flatMap((party) => (party?.kind === 'item' ? [party.itemId] : []));
		}
		// D234: the weapon named after `with` is carried or refused by the handler; a carried thing is in every catalog.
		case 'attack': {
			return action.weaponItemId === undefined ? [] : [action.weaponItemId];
		}
		case 'go': {
			// D31: a destination-only go references its named item — it re-validates
			// against the catalog like any other reference.
			return action.destinationItemId === undefined ? [] : [action.destinationItemId];
		}
		case 'inspect_set': {
			return action.itemIds;
		}
		// D252: the things an order names, for the body to act on — referable here, as the parser found them.
		case 'order': {
			return [action.directObjectItemId, action.indirectObjectItemId, action.toolItemId].filter((itemId): itemId is ItemId => itemId !== undefined);
		}
		case 'use': {
			return action.targetItemId === undefined ? [action.instrumentItemId] : [action.instrumentItemId, action.targetItemId];
		}
	}
};
